<template>
  <div>
    <zm-banner show-close v-if="isT2P" class="opc-sdk-trial2paid-tip" type="info" :show-icon="false">
      <div class="opc-sdk-trial2paid-tip__message">{{ $t('billing.video_sdk_uc.trial2paid_tip',[selectedCredits]) }}</div>
    </zm-banner>
    <config-base-comp
      :show-plan-details="showPlanDetails"
      class="opc-sdk-uc"
      @view-details="viewDetails">
      <template #config>
      <div class="opc-sdk-uc__config">
        <sdk-credit-group
          :group-value="value"
          :category-list="creditCategoryList"
          :select-group-title="$t('billing.video_sdk_uc.select_plan_title')"
          :select-group-desc="$t('billing.video_sdk_uc.more_credits_desc', [maxCreditValue])"
          :ax-select-group-title="title"
          :trackingPlanName="trackingPlanName"
          :unit="planUnitMap.credits"
          group-category-key="plan_code"
          @switchCategoryAction="switchCategoryAction"
        />
      </div>
      </template>
      <template #footer>
      <div class="opc-sdk-uc__footer" v-if="ifShowFT">
        <span class="opc-sdk-uc__footer--desc">{{ $t('billing.video_sdk_uc.freetrial_desc') }}</span>
        <zm-button data-testid="checkout-button-86" class="opc-sdk-uc__footer--switch" type="text" @click="handleSwitch">
          {{ $t('billing.video_sdk_uc.freetrial_cta', [freeTrialCredit]) }}
        </zm-button>
      </div>
      </template>
    </config-base-comp>
  </div>
</template>

<script type="text/jsx" lang="jsx">
import _ from 'lodash';
import ConfigBase from '@/components/config-base';
import mxPlanConfig from '@/plans/VIDEO_SDK_UC/mixin';
import ConfigCustomGroup from '@/components/service/config-custom-group/index';
import { plansof } from '@/utils/chain';
import { mapActions, mapGetters } from 'vuex';
import { getNewPlanUnitWithQuantity, planUnitMap } from '@/common/product-unit';
import { isEmptyVal } from '@/utils';
import { isInrCurrency } from '@/utils/currency';
import { getFreeTrialPlansByCampaignName } from '@/apis';
import { CAMPAIGN_CODES, planTypes } from '@/common/constants';
import { flowTypes } from '@/common/service/page-config';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import PlanDetails from '@/plans/VIDEO_SDK_UC/dialog/plan-details';
import UsageTypeMixins from '@/views/checkout/address/mx-usage-type';
import { logger } from '@/utils/log';
import { BuildPlatformRateDialog } from '@zoom/fe-billing-components'
import { getPlanCollapseCycleDesc } from '@/common/product';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

const ConfigBaseComp = {
  name: 'sdk-uc-base',
  extends: ConfigBase,
  mixins: [mxPlanConfig],
  props: {
    showPlanDetails: Boolean,
    planDetailsLabel: String,
  },
  data() {
    return {
      showLearnMore: false,
      unit: planUnitMap.credits,
    };
  },
  computed: {
    ...mapGetters(['enableBuildPlatform']),
  },
  methods: {
    showCreditRate() {
      this.creditRateFeatureDialog.confirm({
        externalUsage: {
          adp_minute: {
            redirectDataTestId: 'checkout-button-89',
            onClick: () => {
              this.$tracking.buyInteraction(
                'credit rates',
                `${getTrackingProductNameByPlanType(planTypes.VIDEO_SDK_UC)} - audio conferencing - see rate`
              );
            },
          },
          RecordingTransc: {
            hidden: !window.newBillingConfig?.enableVideoSDKAITranscription,
          },
          RecordingSummary: {
            hidden: !window.newBillingConfig?.enableVideoSDKAITranscription,
          },
          Stream: {
            hidden: !window.newBillingConfig?.enableUniversalBundleSupportRtms,
          },
          Transcription: {
            hidden: !window.newBillingConfig?.enableUniversalBundleSupportRtms,
          },
          ScribeFast: {
            hidden: !window.newBillingConfig?.enableUniversalBundleSupportScribeApi,
            paidOnly: !window.newBillingConfig.enableUniversalBundleSupportScribeApiForFree,
          },
          ScribeBatch: {
            hidden: !window.newBillingConfig?.enableUniversalBundleSupportScribeApi,
            paidOnly: true,
          },
          ScribeDiazation: {
            hidden: !window.newBillingConfig?.enableUniversalBundleSupportScribeApi,
            paidOnly: false,
          },
        },
        enableBuildPlatform: !!this.enableBuildPlatform,
        onClose: () => {
          this.$tracking.buyInteraction(
            'product details',
            `credit rates - close`
          );
        },

      }).catch((error) => {
        logger.error('showCreditRate dialog closed', error);
      });
      this.$tracking.buyInteraction(
        'credit rates selected',
        getTrackingProductNameByPlanType(planTypes.VIDEO_SDK_UC),
      );
    },
    mx_renderLicense(plan) {
      if (!plan.cartInfo.selected_number) return '';
      const unitDesc = getNewPlanUnitWithQuantity(this.unit, plan.credit);
      const amount = this.$money(plan.amount + (plan?.esmd_amount || 0));
      return `${unitDesc} • ${amount}`;
    },
    mx_renderCycle(plan) {
      const billingCycle = plan?.billing_cycle;
      const cycleDesc = getPlanCollapseCycleDesc(billingCycle);

      return cycleDesc || '';
    },
    mx_renderConfigDesc() {
      return <div class="opc-zm-base-card__main--desc">
        <span class="opc-zm-base-card__main--desc__content" innerHTML={this.subtitle} />
        <zm-button data-testid="checkout-button-87" type="text" onClick={this.showCreditRate} class="opc-zm-base-card__main--desc__btn">
          {this.$t('billing.video_sdk_uc.credit_rates_cta')}
        </zm-button>
      </div>;
    },
  },
  created() {
    // this.creditRateFeatureDialog = new ServiceConfirm(CreditRateFeature);
    this.creditRateFeatureDialog = new ServiceConfirm(BuildPlatformRateDialog);
  },
};

const SdkCreditGroup = {
  extends: ConfigCustomGroup,
  methods: {
    mx_renderGroupCol(item = {}) {
      const { plan = {}, disabled } = item;

        return <zm-radio data-testid="checkout-radio-88" disabled={disabled} label={plan.plan_code}>
          <p class="opc-custom-group__plan__title">
            <span>{getNewPlanUnitWithQuantity(planUnitMap.credits, plan.credit)}</span>
          </p>
        <p class="opc-custom-group__plan__price">
          <div class="opc-custom-group__plan__price--amount">
            {this.$money(plan.amount, { forceDiff: true })}
          </div>
          <div class="opc-custom-group__plan__price--unit">
            {this.$t('billing.common.per_billing_cycle.monthly')} ({ this.$t('billing.bill_monthly') })<priceVATTip />
          </div>
        </p>
      </zm-radio>;
    },
  },
};

export default {
  components: {
    ConfigBaseComp,
    SdkCreditGroup,
  },
  mixins: [mxPlanConfig, UsageTypeMixins],
  data() {
    return {
      value: '',
      planUnitMap,
      freeTrialCredit: '',
    };
  },
  computed: {
    ...mapGetters(['planMapFlat', 'getSelectedPlans','enableSDKFreeTrial']),
    isEnableFTSDK() {
      return !!this.enableSDKFreeTrial
    },
    showPlanDetails() {
      return true;
    },
    planList() {
      return plansof(this.planMapFlat).type(this.planType).get();
    },
    creditCategoryList() {
      const length = this.planList?.length || 0;

      return _.chain(this.planList)
        .orderBy('credit')
        .map((plan, index) => {
          const isLast = index === length - 1;

          return {
            plan,
            disabled: false,
            disabledTooltip: '',
            savedPercentValue: isLast ? '10%' : undefined,
          };
        })
        .value();
    },
    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },
    selectedCredits(){
      return this.selectedPlan?.credit
    },
    isT2P(){
      return this.$context.is.trial2paid
    },
    maxCreditValue() {
      return this.creditCategoryList?.[1]?.plan?.credit || 500;
    },
    ifShowFT() {
      return !this.isT2P && this.isEnableFTSDK && !isInrCurrency() && !this.$config('isMasterOrSubAccount') && this.$context.flow !== flowTypes.sdkpayg2uc;
    },
  },
  methods: {
    ...mapActions(['switchPlanByTypeAction']),
    viewDetails() {
      this.planDetailsDialog.confirm({
        title: this.title,
      }).catch((error) => {
        logger.error('viewDetails dialog closed', error);
      });

      this.$tracking.buyInteraction(
        'plan details selected',
        getTrackingProductNameByPlanType(planTypes.VIDEO_SDK_UC),
      );
    },
    switchCategoryAction(oldPlan,newPlan) {
      this.switchPlanByTypeAction([{
        ...newPlan,
        cartInfo: {
          selected_number: 1,
        },
      }]);
      this.$tracking.buyInteraction(getTrackingProductNameByPlanType(planTypes.VIDEO_SDK_UC),newPlan?.credit+' credits');
    },
    handleSwitch() {
      this.$tracking.buyInteraction('explore other plans', `${this.trackingPlanName} - switch now to pay as you go`);
      location.href = `/opc/buy/config?type=buy_trial&plan=zsdkuc&from=zsdkuc&period=monthly&campaign_code=${CAMPAIGN_CODES.VIDEO_SDK}&trial_code=zoom_sdk_trial_service`;
    },
    async getFreeTrialCredit() {
      const { result = [] } = await getFreeTrialPlansByCampaignName(CAMPAIGN_CODES.VIDEO_SDK).catch(() => ({}));
      this.freeTrialCredit = result[0]?.credit || '20';
    },
  },
  async created() {
    if (!isEmptyVal(this.selectedPlan)) {
      this.value = this.selectedPlan?.plan_code;
    }

    this.planDetailsDialog = new ServiceConfirm(PlanDetails);

    // Get free trial credit from VIDEO_SDK campaign
    await this.getFreeTrialCredit();
  },
};
</script>

<style lang="scss">
.opc-sdk-uc {

  .opc-zm-base-card__main__footer {
    margin-top: 16px;
  }

  &__group {
    text-align: left;
    &--desc {
      margin: 5px 0;
      font-weight: 400;
      font-size: 14px;
      font-style: normal;
      line-height: 150%;
      color: var(--zoom-color-text-strong-neutral, #555B62);
    }
  }

  &__footer {
    text-align: left;

    &--desc, &--switch {
      font-size: 14px;
      font-style: normal;
      line-height: 150%;
    }

    &--desc {
      font-weight: 400;
      color: var(--zoom-color-text-strong-neutral, #555B62);
    }

    &--switch {
      margin-left: 4px;
    }
  }
}
.opc-sdk-trial2paid-tip {
  margin-bottom: 16px;

  &__message {
    flex-grow: 1;
    margin-left: 16px;
    line-height: 1.5;
    font-size: 14px;
    font-weight: 450;
    font-style: normal;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  }
}
</style>
